import { WIDGET_DEFAULT_FONT } from 'constants/fonts';
import { WidgetId } from 'types/idTypes';
import { Subset } from 'types/object.types';
import { WidgetType } from 'types/widget.types';
import { generateDefaultData as generateIconData } from 'widgets/IconWidget/IconWidget.helpers';
import { IconWidgetData, IconWidgetType } from 'widgets/IconWidget/IconWidget.types';
import {
  DEFAULT_BAR_STAT_HEIGHT,
  DEFAULT_WIDTH as DEFAULT_DONUT_HEIGHT,
  DEFAULT_HEIGHT as DEFAULT_HALF_DONUT_HEIGHT,
} from 'widgets/ProgressChartWidget/ProgressChartWidget.config';
import { generateDefaultData as generateProgressChartData } from 'widgets/ProgressChartWidget/ProgressChartWidget.helpers';
import { ProgressChartType, ProgressChartWidgetData } from 'widgets/ProgressChartWidget/ProgressChartWidget.types';
import { generateDefaultData as generateMetricTextData } from 'widgets/TextBasedWidgets/LabelTextWidget/LabelTextWidget.helpers';
import {
  FontWeightOption,
  LabelTextStyle,
  LabelTextWidgetData,
} from 'widgets/TextBasedWidgets/LabelTextWidget/LabelTextWidget.types';
import { WidgetMap } from 'widgets/Widget.types';

import {
  CHART_TYPE_DEFAULT_DIMENSIONS_MAP,
  DEFAULT_50_ICON_HEIGHT_PX,
  DEFAULT_FILL_PERCENTAGE,
  DEFAULT_FONT_SIZE,
  DEFAULT_FONT_WIDTH_PX,
  DEFAULT_ICON_FILL_PERCENTAGE,
  DEFAULT_ICON_HEIGHT_PX,
  DEFAULT_ICON_STAT_WIDTH_PX,
  DEFAULT_ICON_WIDTH_PX,
  DEFAULT_PRIMARY_CHART_COLOR,
  DEFAULT_SECONDARY_CHART_COLOR,
  DEFAULT_VERTICAL_SPACING,
  ICON_DEFAULTS,
  TEXT_ICON_GAP,
} from './StatChartWidget.config';
import {
  ComponentWidgetIdKeys as StatChartComponentKey,
  StatChartOptions,
  StatChartType,
  StatChartWidgetData,
} from './StatChartWidget.types';
import { VERSION } from './StatChartWidget.upgrade';

/**
 * Helper to calculate the maximum font size that will prevent the `text` from wrapping
 * in a div with the specified `width`
 * Uses canvas context.measureText method to measure width - doesn't actually render
 * the text.
 *
 * @param text text content
 * @param textStyle text style (bold, underline, italic, font-family)
 * @param proposedFontSize
 * @param width Width that text must fit into
 * @returns font size that will prevent the `text` from wrapping in the given `width`
 */
const calculateFontSizeFromWidth = (
  text: string,
  textStyle: LabelTextStyle,
  proposedFontSize: number,
  width: number,
) => {
  const FONT_SIZE_DECREMENT_STEP = 2;
  const { fontFamily, fontWeight, fontStyle } = textStyle;

  const element = document.createElement('canvas');
  const context = element.getContext('2d');
  if (!context) return proposedFontSize;

  context.font = `${fontStyle} normal ${fontWeight} ${proposedFontSize}px ${fontFamily}`;
  let textWidth = context.measureText(text).width;

  if (textWidth <= width) {
    return proposedFontSize;
  }

  let currFontSize = proposedFontSize;
  while (textWidth > width && currFontSize >= FONT_SIZE_DECREMENT_STEP + 1) {
    currFontSize -= FONT_SIZE_DECREMENT_STEP;
    context.font = `${fontStyle} normal ${fontWeight} ${currFontSize}px ${fontFamily}`;
    textWidth = context.measureText(text).width;
  }

  element.remove();

  return currFontSize || proposedFontSize;
};

/**
 * Helper to calculate the maximum font size that will prevent the `text` from wrapping
 * in a div with the specified `width`
 * Uses canvas context.measureText method to measure width - doesn't actually render
 * the text.
 *
 * @param text text content
 * @param textStyle text style (bold, underline, italic, font-family)
 * @param proposedFontSize
 * @param width Width that text must fit into
 * @returns font size that will prevent the `text` from wrapping in the given `width`
 */
const calculateWidthFromFontSize = (
  text: string,
  fontStyle: LabelTextStyle['fontStyle'],
  fontFamily: LabelTextStyle['fontFamily'],
  fontWeight: LabelTextStyle['fontWeight'],
  newFontSize: number,
) => {
  let textWidth = newFontSize;
  const WIDTH_CONSTANT = 1.17;

  const element = document.createElement('canvas');
  const context = element.getContext('2d');

  if (context !== null) {
    context.font = `${fontStyle} normal ${fontWeight} ${newFontSize}px ${fontFamily}`;
    textWidth = context.measureText(text + '%').width;
  }

  element.remove();

  return textWidth * WIDTH_CONSTANT || newFontSize;
};

const getProgressChartType = (chartType: StatChartType, options?: StatChartOptions) => {
  switch (chartType) {
    case StatChartType.Donut:
      return options?.showLabel ? ProgressChartType.DonutStat : ProgressChartType.Donut;
    case StatChartType.HalfDonut:
      return options?.showLabel ? ProgressChartType.HalfDonutStat : ProgressChartType.HalfDonut;
    case StatChartType.ProgressBar:
      return options?.showLabel ? ProgressChartType.BarStat : ProgressChartType.Bar;
    default:
      return ProgressChartType.Donut;
  }
};

/**
 * Helper to generate new ProgressChartWidget and MetricTextWidget
 * update data after swapping stat chart types.
 *
 * Returns update data to be passed to updateWidget() fn.
 *
 * @param oldType old chart type
 * @param newType new chart type to swap to
 * @param oldChartData progress chart widget data
 * @param oldIconData icon widget data
 * @returns update data for chart and metric text widgets
 */
const swapChartType = (
  oldType: StatChartType,
  newType: StatChartType,
  oldChartData: ProgressChartWidgetData,
  oldIconData: IconWidgetData,
  metricTextWidgetData?: LabelTextWidgetData,
) => {
  const { widthPx, heightPx } = oldChartData;
  const options = { showLabel: metricTextWidgetData?.showLabel };
  let newChartWidgetData: Subset<ProgressChartWidgetData> = {};
  let newMetricTextWidgetData: Subset<LabelTextWidgetData> = {};
  let newIconWidgetData: Subset<IconWidgetData> = {};
  let newWidgetData: Subset<StatChartWidgetData> = {
    type: newType,
    options,
  };

  // If swapping from icon statchart to another type,
  // reset the size for the new chart type to the default size
  if (oldType === StatChartType.Icon) {
    const statChart = generateDefaultData(newType, {
      ...options,
      type: getProgressChartType(newType, options),
    });
    const [chart, metricText] = statChart.groupWidgets;

    newChartWidgetData = {
      // apply the icon colors as chart colors
      dataColor: oldIconData.shapeColorOne,
      nonDataColor: oldIconData.shapeColorTwo,
      // reset the chart widget size
      barHeight: chart.widgetData.barHeight,
      heightPx: chart.widgetData.heightPx,
      widthPx: chart.widgetData.widthPx,
      type: chart.widgetData.type,
      donutSize: chart.widgetData.donutSize,
      isHidden: false,
    };

    // reset the text label widget size
    newMetricTextWidgetData = {
      hasFontSizeOption: metricText.widgetData.hasFontSizeOption,
      widthPx: metricText.widgetData.widthPx,
      heightPx: metricText.widgetData.heightPx,
      style: { fontSize: metricText.widgetData.style.fontSize },
      isHidden: false,
      showLabel: options?.showLabel,
    };

    newIconWidgetData = {
      isHidden: true,
    };

    // set size of statchart widget to the same as the default size
    newWidgetData.widthPx = statChart.widgetData.widthPx;
    newWidgetData.heightPx = statChart.widgetData.heightPx;

    return { newChartWidgetData, newMetricTextWidgetData, newIconWidgetData, newWidgetData };
  }

  switch (newType) {
    case StatChartType.Donut: {
      const newChartDimension = Math.max(widthPx, heightPx);
      newChartWidgetData = {
        widthPx: newChartDimension,
        heightPx: newChartDimension,
        type: ProgressChartType.Donut,
      };
      newMetricTextWidgetData = { hasFontSizeOption: false, showLabel: options?.showLabel };
      break;
    }
    case StatChartType.HalfDonut: {
      newChartWidgetData = {
        heightPx: widthPx / 2,
        type: ProgressChartType.HalfDonut,
      };
      newMetricTextWidgetData = { hasFontSizeOption: false, showLabel: options?.showLabel };
      break;
    }
    case StatChartType.ProgressBar: {
      // Reset bar height to default of bar height
      const ratio =
        oldType === StatChartType.Donut
          ? DEFAULT_BAR_STAT_HEIGHT / DEFAULT_DONUT_HEIGHT
          : DEFAULT_BAR_STAT_HEIGHT / DEFAULT_HALF_DONUT_HEIGHT;

      newChartWidgetData = {
        heightPx: heightPx * ratio,
        type: ProgressChartType.Bar,
      };

      newMetricTextWidgetData = { widthPx, hasFontSizeOption: true, showLabel: options?.showLabel };
      break;
    }
    case StatChartType.Icon: {
      const statChart = generateDefaultData(newType, options);
      const [, , gridIcon] = statChart.groupWidgets;

      // apply the chart colors as icon colors
      newIconWidgetData = {
        shapeColorOne: oldChartData.dataColor,
        shapeColorTwo: oldChartData.nonDataColor,
        // reset the rest of the icon to default
        widthPx: gridIcon.widgetData.widthPx,
        heightPx: gridIcon.widgetData.heightPx,
        numberOfIcons: gridIcon.widgetData.numberOfIcons,
        iconId: gridIcon.widgetData.iconId,
        gridItemWidthPx: gridIcon.widgetData.gridItemWidthPx,
        gridItemHeightPx: gridIcon.widgetData.gridItemHeightPx,
        gridGapPx: gridIcon.widgetData.gridGapPx,
        isHidden: false,
      };

      // update to default size for text
      newMetricTextWidgetData = {
        hasFontSizeOption: true,
        widthPx: DEFAULT_FONT_WIDTH_PX,
        heightPx: DEFAULT_FONT_SIZE,
        style: { fontSize: DEFAULT_FONT_SIZE },
        isHidden: false,
        showLabel: options?.showLabel,
      };

      newChartWidgetData = {
        isHidden: true,
      };

      // set size of statchart widget to the same as the default size
      const { widthPx: newWidthPx, heightPx: newHeightPx } = CHART_TYPE_DEFAULT_DIMENSIONS_MAP[newType].widgetDimension;
      newWidgetData.widthPx = newWidthPx;
      newWidgetData.heightPx = newHeightPx;
    }
  }

  return { newChartWidgetData, newMetricTextWidgetData, newIconWidgetData, newWidgetData };
};

const generateDefaultData = (type: StatChartType, options?: StatChartOptions) => {
  let progressChartWidgetType = ProgressChartType.DonutStat;
  switch (type) {
    case StatChartType.HalfDonut:
      progressChartWidgetType = ProgressChartType.HalfDonutStat;
      break;
    case StatChartType.ProgressBar:
      progressChartWidgetType = ProgressChartType.BarStat;
      break;
    default:
      break;
  }

  if (options?.type && ProgressChartType[options?.type]) progressChartWidgetType = options?.type;

  const { widthPx, heightPx } = CHART_TYPE_DEFAULT_DIMENSIONS_MAP[type].widgetDimension;
  const isProgressBar = type === StatChartType.ProgressBar;
  const isIcon = type === StatChartType.Icon;
  const hasLabel = options?.showLabel !== undefined ? options?.showLabel : true;
  const defaultIcon = ICON_DEFAULTS[hasLabel ? 'IconStat' : 'IconStatNoLabel'];
  const defaultValue = (isIcon && hasLabel) || !isIcon ? DEFAULT_FILL_PERCENTAGE : DEFAULT_ICON_FILL_PERCENTAGE;

  return {
    version: VERSION,
    widgetType: WidgetType.StatChart,
    widgetData: {
      widthPx,
      heightPx,
      topPx: 200,
      leftPx: 200,
      rotateDeg: 0,
      version: VERSION,

      memberWidgetIds: [],
      componentWidgetIdMap: {},

      altText: '',
      isDecorative: false,
      // stat chart data
      type,
      verticalSpacing: DEFAULT_VERTICAL_SPACING,
      options,
    },
    groupWidgets: [
      {
        // Progress chart
        ...generateProgressChartData(progressChartWidgetType, { isHidden: type === StatChartType.Icon }),
        componentKey: StatChartComponentKey.Chart,
      },
      {
        // Metric Text Widget
        ...generateMetricTextData({
          styleOptions: {
            fontFamily: WIDGET_DEFAULT_FONT,
            fontSize: DEFAULT_FONT_SIZE,
            color: DEFAULT_PRIMARY_CHART_COLOR,
            fontWeight: FontWeightOption.Bold,
          },
          metricOptions: {
            value: `${defaultValue}`,
            suffix: '%',
            isNumeric: true,
            hasFontSizeOption: isProgressBar || isIcon,
            widthPx: DEFAULT_FONT_WIDTH_PX,
            heightPx: DEFAULT_FONT_SIZE,
            showLabel: hasLabel,
          },
        }),
        componentKey: StatChartComponentKey.MetricText,
      },
      {
        // Grid Icon widget
        ...generateIconData(
          defaultIcon,
          '',
          undefined,
          undefined,
          undefined,
          {
            widthPx: hasLabel ? DEFAULT_ICON_STAT_WIDTH_PX : DEFAULT_ICON_WIDTH_PX,
            heightPx: hasLabel ? DEFAULT_50_ICON_HEIGHT_PX : DEFAULT_ICON_HEIGHT_PX,
            shapeFill: defaultValue,
            shapeColorOne: DEFAULT_PRIMARY_CHART_COLOR,
            shapeColorTwo: DEFAULT_SECONDARY_CHART_COLOR,
            type: IconWidgetType.Grid,
            isHidden: type !== StatChartType.Icon,
          },
          undefined,
          { showLabel: hasLabel },
        ),
        componentKey: StatChartComponentKey.IconGrid,
      },
    ],
    isResponsiveGroup: true,
  };
};

/**
 * Get the max translateX value for the statchart when resizing from the side handle,
 * E.g. the point where it can't be moved any further to the right
 *
 * @param responsiveWidgetBaseId WidgetId
 * @param responsiveWidgetWidth number - the width of the statchart itself
 * @param widgets WidgetMap
 *
 * @returns {null | number}
 */
const getMaxTranslateX = (responsiveWidgetBaseId: WidgetId, responsiveWidgetWidth: number, widgets: WidgetMap) => {
  const statChartWidgetData = widgets[responsiveWidgetBaseId] as StatChartWidgetData;
  if (!statChartWidgetData) return null;
  const type = statChartWidgetData.type;

  // Only icons has a maxWidth - can be be extended here for the other types
  if (type !== StatChartType.Icon) return null;

  const iconId = statChartWidgetData.componentWidgetIdMap.iconGrid;
  const labelId = statChartWidgetData.componentWidgetIdMap.metricText;

  const metricTextData = widgets[labelId];
  const iconSize = (widgets[iconId] as IconWidgetData).gridItemWidthPx;
  if (!metricTextData || !iconSize) return null;

  // if the label is hidden the max is the widget width - icon size
  if (metricTextData.isHidden) {
    const maxX = responsiveWidgetWidth - iconSize;
    return Math.max(maxX, 0);
  }

  // we need to use the real width here as it's fixed and not the same as the widthPx
  const metricWidth = document.getElementById(labelId)?.clientWidth || 0;
  const maxX = responsiveWidgetWidth - (metricWidth + TEXT_ICON_GAP) - iconSize;
  return Math.max(maxX, 0);
};

/**
 * Checks if the label widget member of the statchart should be hidden.
 * Accounts for old statcharts that don't have the isHidden property on the label widget itself
 * by checking the metricTextEnabled flag - see StatChartWidget.changelog.md
 *
 * If `isWidgetHidden` is a boolean, it returns its value.
 * If `metricTextEnabled` is a boolean, it returns the negation of its value.
 * Otherwise, it returns false.
 *
 * @param {Object} options - The options object.
 * @param {boolean | undefined} options.isWidgetHidden - The flag indicating if the widget is hidden.
 * @param {boolean | undefined} options.metricTextEnabled - The flag indicating if the metric text is enabled.
 * @returns {boolean} - True if the label should be hidden, false otherwise.
 */
export const isLabelHidden = ({
  isWidgetHidden,
  metricTextEnabled,
}: {
  isWidgetHidden: undefined | boolean;
  metricTextEnabled: undefined | boolean;
}) => {
  if (typeof isWidgetHidden === 'boolean') return isWidgetHidden;
  if (typeof metricTextEnabled === 'boolean') return !metricTextEnabled;

  return false;
};

export { calculateFontSizeFromWidth, calculateWidthFromFontSize, generateDefaultData, getMaxTranslateX, swapChartType };
